import { useEffect } from "react";
import { supabase } from "../lib/supabase";
import type { RealtimeChannel } from "@supabase/supabase-js";

/** One subscribed sender channel per event, kept for the lifetime of the page. */
const senders = new Map<string, RealtimeChannel>();

function sender(eventId: string): RealtimeChannel {
  let channel = senders.get(eventId);
  if (!channel) {
    channel = supabase.channel(`carousel-${eventId}`);
    senders.set(eventId, channel);
    channel.subscribe();
  }
  return channel;
}

/**
 * Console-side sender on the stage's broadcast topic.
 *
 * The topic must stay suffix-free (`carousel-${eventId}`) because the stage
 * listens on exactly that name — so this hook is the documented exemption to
 * the per-instance-UUID channel-naming rule in CLAUDE.md. Without a suffix the
 * usual per-mount create/remove is unsafe: `supabase.channel(topic)` returns
 * the *existing* instance with that topic and `removeChannel()` is async, so a
 * fast unmount/remount hands the new mount a channel that is already being
 * torn down, leaving a dead channel behind that silently swallows every step.
 *
 * The fix is to cache instead of recreate: get-or-create one channel per event,
 * subscribe it once, and never remove it. The console page's lifetime is the
 * channel's lifetime, and a single broadcast sender per event costs nothing.
 */
export function useStageRemote(eventId: string): { step: (dir: "next" | "prev") => void } {
  useEffect(() => {
    sender(eventId);
  }, [eventId]);

  return {
    step: (dir) => {
      void sender(eventId).send({ type: "broadcast", event: dir, payload: {} });
    },
  };
}
